import React from 'react';
import axios from 'axios';
import ReactCrop, { makeAspectCrop } from 'react-image-crop';
import { ErrorBoundary } from "@appsignal/react";
import Loader from '../Loader/Loader';
import Alert from '../Alert';
import Modal from '../Modal/Modal';
import IImageUploader, { ICrop, TImageUploader } from './IImageUploader';
import noPhotoMale from '@images/no_photo_male.png';
import noPhotoCompany from '@images/no_photo_company.png';

import { appsignal } from "./../../appsignal";
import * as style from './ImageUploaderStyles.module.scss'

const compabilityCheck = () => {
  const compatibile = ['Blob', 'FileReader', 'Promise', 'FormData', 'FileReader', 'Image'].map(api => (
    api in window
  ));
  return !compatibile.includes(false);
};
// TODO: Improve usage of BttfAxios here
const axiosFormToken = axios.create();

const rerenderPreviewParts = (type: string , fileUrl: string | null, model: string) => {

  if (type === 'avatar' && model === 'profile_image') {
    const profileAvatar = document.querySelectorAll('img.js-profile_avatar');
    let newFile = `${fileUrl}?rand='${Math.floor(Math.random() * 100)}`;
    if (!fileUrl) {
      newFile = noPhotoMale;
    }

    if (profileAvatar.length > 0) {
      Array.from(profileAvatar).forEach((img: HTMLImageElement) => {
        img.src = newFile;
      });
    }
  } else if (type === 'avatar' && model === 'logo') {
    const companyImg: NodeListOf<HTMLImageElement> = document.querySelectorAll('img.js-company-box-info__logo-image');
    if (companyImg.length > 0) {
      companyImg[0].src = fileUrl || noPhotoCompany;
    }
  }
};

class ImageUploader extends React.Component<IImageUploader, TImageUploader> {

  state: TImageUploader = {
    selectedFile: null,
    uploading: false,
    preview: this.props.preview,
    proceedCrop: false,
    currentImageUrl: this.props.image || null,
    errors: [],
    crop: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
  };

  private inputElement: HTMLInputElement;

  onCropChange = (crop: ICrop) => {
    this.setState({ crop });
  }

  onImageLoaded = (image: HTMLImageElement) => {
    const aspect = this.props.aspect === 'panorama' ? 16 / 9 : 1;
    this.setState({
      crop: makeAspectCrop({
        aspect,
        x: 0,
        y: 0,
        width: 50,
        height: 50,
      }, image.width / image.height),
    });
  }

  getCroppedImg(image: HTMLImageElement, crop: ICrop, fileName: string) {
    const ratioW = image.naturalWidth / image.width;
    const ratioH = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    canvas.width = crop.width * ratioW;
    canvas.height = crop.height * ratioH;

    ctx.drawImage(
      image,
      crop.x * ratioW,
      crop.y * ratioH,
      crop.width * ratioW,
      crop.height * ratioH,
      0,
      0,
      crop.width * ratioW,
      crop.height * ratioH
    );

    return new Promise((resolve) => {
      canvas.toBlob((file: any) => {
        file.name = fileName;
        resolve(file);
      }, 'image/jpeg');
    });
  }

  fileSelectedHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();

    const reader: FileReader = new FileReader();
    const file = (event.target as any).files[0];

    reader.onloadend = () => {
      const imageTypes = ['image/jpeg', 'image/png'];

      if (imageTypes.includes(file.type)) {
        this.setState({
          selectedFile: file,
          uploading: false,
          proceedCrop: true,
          currentImageUrl: String(reader.result),
        });
      } else {
        this.setState({
          uploading: false,
          proceedCrop: false,
          errors: ['Unsupported format. Please upload jpg/png file.'],
        });
      }

    };
    reader.readAsDataURL(file);
  }

  fileUploadHandler = (cb: (() => void) | boolean | undefined) => {
    this.setState({
      uploading: true,
      preview: false,
      proceedCrop: false,
    });
    this.cropping();

    if (cb && typeof (cb) !== 'boolean') {
      cb();
    }
  }

  deleteImageHandler(cb: (() => void) | boolean) {
    const { token } = this.props;
    this.setState({
      uploading: true,
      preview: false,
    });

    axiosFormToken.delete(this.props.deleteUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': token,
        'Cache-Control' : 'no-cache',
      },
    })
      .then(() => {
        this.setState({
          uploading: false,
          currentImageUrl: '',
          selectedFile: null,
        });
        rerenderPreviewParts(this.props.type, null, this.props.model_attr);
        if (typeof (cb) !== 'boolean') {
          cb();
        }
      })
      .catch((err) => {
        this.setState({
          uploading: false,
          errors: err.response.data.error || err,
        });
      });
  }

  closeErrors() {
    this.setState({
      errors: [],
    });
  }

  cancelImage() {
    this.setState({
      proceedCrop: false,
      selectedFile: null,
      currentImageUrl: '',
    });
  }

  uploadFile(data: FormData) {
    const { token } = this.props;
    axiosFormToken.post(this.props.uploadUrl, data, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': token,
        'Cache-Control': 'no-cache',
      },
    })
      .then((resp) => {
        this.setState({
          uploading: false,
          preview: true,
          proceedCrop: false,
          currentImageUrl: `${resp.data.files[0].url}?rand='${Math.floor(Math.random() * 100)}`
            .replace('/origin/', '/original/'),
        }, (
        () => {
          rerenderPreviewParts(this.props.type, resp.data.files[0].url, this.props.model_attr);
        }));
      })
      .catch((err: any) => {
        this.setState({
          uploading: false,
          proceedCrop: false,
          preview: false,
          errors: err.response.data.error || [err.response.statusText] || err,
        });
      });
  }

  cropping() {
    const reader = new FileReader();
    const draw = (image: HTMLImageElement) => {
      this.constructor.prototype.getCroppedImg(
        image, this.state.crop, this.state.selectedFile.name
      ).then((resp: Blob) => {
        const formData: FormData = new FormData();
        formData.append(this.props.model_attr, resp, this.state.selectedFile.name);
        this.uploadFile(formData);
      });
    };

    reader.addEventListener('load', () => {
      const image = new Image();
      image.width = 100;
      image.height = 100;
      image.src = String(reader.result);
      draw(image);
    }, false);

    reader.readAsDataURL(this.state.selectedFile);
  }

  render() {
    const fileUploadSection = (
      <div
        className={style.wrapper}
        role="presentation"
        onClick={() => this.inputElement.click()}
      >
        <input className={style.input} placeholder="JPG/PNG only" />
        <div className="hide">
          <input
            type="file"
            ref={(input) => { this.inputElement = input as HTMLInputElement; }}
            onChange={this.fileSelectedHandler}
          />
        </div>
      </div>
    );

    const errorMessagesList = () => (
      this.state.errors.map(error => (
        <p key={error}>{error}</p>
      ))
    );

    const uploadButton = () => (
      <div>
        <button
          type="button"
          className={`${style.button} btn btn-warning`}
          onClick={() => this.cancelImage()}
        >Close
        </button>
        <button
          type="button"
          className={`${style.button} btn btn-success`}
          onClick={() => this.fileUploadHandler(this.props.fileUploaded)}
        >Upload
        </button>
      </div>
    );

    const renderState = () => {
      let wrapper = fileUploadSection;

      if (this.state.preview) {
        wrapper = (
          <div className={style[this.props.type]}>
            <img
              src={this.state.currentImageUrl}
              alt=""
              className={`${this.props.type === 'avatar' ? 'img-circle' : 'img-rounded'} img-responsive`}
            />
            <button
              className={`${style.button} btn btn-warning m-t-1`}
              onClick={() => this.deleteImageHandler(this.props.fileRemoved)}
            >Delete
            </button>
            <div className="clearfix" />
          </div>
        );
      }

      if (this.state.uploading) {
        wrapper = <Loader />;
      }

      if (this.state.proceedCrop) {
        wrapper = (
          <Modal
            header="Crop photo"
            show={this.state.proceedCrop}
            buttons={uploadButton()}
            cancel={() => this.cancelImage()}
          >
            <div className={style.modalContent}>
              <ReactCrop
                src={this.state.currentImageUrl}
                crop={this.state.crop}
                onChange={(crop: ICrop) => this.onCropChange(crop)}
                onImageLoaded={(image: HTMLImageElement) => this.onImageLoaded(image)}
              />
            </div>
          </Modal>
        );
      }

      if (this.state.errors.length > 0) {
        const alert = {
          type: 'danger',
          text: errorMessagesList(),
        };
        wrapper = <Alert alert={alert} close={() => this.closeErrors()} />;
      }

      return wrapper;
    };


    const renderHTML = () => {
      let result = renderState();
      if (!compabilityCheck() && !this.state.preview) {
        result = (
          <Alert
            close={() => this.closeErrors()}
            alert={{ type: 'warning', text: 'No browser support. Please update browser.' }}
          />
        );
      }
      return result;
    };

    return (
      <ErrorBoundary instance={appsignal}>
        <div>
          {renderHTML()}
        </div>
      </ErrorBoundary>
    );
  }
}

export default ImageUploader;

